import { CreateProjectRequestConfig, serverMessage, useStorytellClient } from "@storytell/client";
import type { CreatableBillingMode, Project, ProjectId } from "@storytell/types";
import { StButton } from "@storytell/ui/buttons";
import { StDialog, StDialogCloseButton, StDialogTitle } from "@storytell/ui/dialog";
import { TbArrowLeft, TbChevronDown, TbCube, TbCubePlus, TbHelpCircle, TbSearch, TbX } from "solid-icons/tb";
import { type Component, createEffect, createMemo, createSignal, For, onCleanup, onMount, Show } from "solid-js";
import { twMerge } from "tailwind-merge";
import type { ProjectsService } from "../../stores/projects/projectsService";
import type { UserContextService } from "../../stores/userContext/userContextService";
import { rankProjects } from "./projectSearch";

type ViewState = "grid" | "form";

/**
 * All translatable strings for the ProjectSwitcherModal.
 * Pass these from the parent component using the i18n system.
 */
interface ProjectSwitcherStrings {
  modalTitle: string;
  addProject: string;
  /** Carries a `{name}` placeholder for the unmatched search text. */
  createNamed: string;
  searchPlaceholder: string;
  recentHeading: string;
  noResults: string;
  currentProject: string;
  back: string;
  createTitle: string;
  createDesc: string;
  organizationLabel: string;
  /** Carries a `{name}` placeholder for the organization the user cannot create in. */
  organizationUnavailable: string;
  createError: string;
  projectName: string;
  learnProjects: string;
  namePlaceholder: string;
  description: string;
  descriptionOptional: string;
  descriptionPlaceholder: string;
  cancel: string;
  createButton: string;
  billingHeading: string;
  billingLearnMore: string;
  billingCallerLabel: string;
  billingCallerDesc: string;
  billingOrgLabel: string;
  billingOrgDesc: string;
  roleCollaborator: string;
  roleReader: string;
}

/** Default English strings for backwards compatibility */
const DEFAULT_STRINGS: ProjectSwitcherStrings = {
  modalTitle: "Projects",
  addProject: "Create project",
  createNamed: 'Create "{name}"',
  searchPlaceholder: "Search projects...",
  recentHeading: "Recent",
  noResults: "No projects match your search.",
  currentProject: "Current",
  back: "Back",
  createTitle: "Create a new project",
  createDesc: "Projects help you organize your work and collaborate with others.",
  organizationLabel: "Organization",
  organizationUnavailable: "{name} is not available: you do not own it.",
  createError: "Could not create the project. Please try again.",
  projectName: "Project name",
  learnProjects: "Learn about projects",
  namePlaceholder: "e.g., Q4 Planning",
  description: "Description",
  descriptionOptional: "(optional)",
  descriptionPlaceholder: "What is this project about?",
  cancel: "Cancel",
  createButton: "Create project",
  billingHeading: "Project plan & billing",
  billingLearnMore: "Learn more",
  billingCallerLabel: "Members use their own plans",
  billingCallerDesc:
    "Each member's usage is charged to their own account and feature access depends on their individual subscription.",
  billingOrgLabel: "Use my plan for everyone",
  billingOrgDesc:
    "All project usage is charged to your account, and members get access to features included in your subscription.",
  roleCollaborator: "Collaborator",
  roleReader: "Reader",
};

interface ProjectSwitcherModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projects: ProjectsService;
  userContext: UserContextService;
  /** The organization a new project lands in by default, and the only target when `createOrganizations` is absent. */
  homeOrganizationId: () => string | undefined;
  authToken: () => string | undefined;
  /** Optional: the organizations the user may create a project in. An empty list hides the selector. */
  createOrganizations?: () => { id: string; name: string }[];
  /** Optional: names the focused organization, supplied only when the user cannot create in it. */
  unavailableOrganizationName?: () => string | undefined;
  /** Optional: Translatable strings. Falls back to English defaults if not provided. */
  strings?: Partial<ProjectSwitcherStrings>;
  /** Optional: names the organization holding a project, for the row's secondary line. */
  organizationName?: (organizationId: string) => string | undefined;
  initialView?: ViewState;
  /** Called when user wants to manage a project (navigate to project detail page). When used after create, return true if switch succeeded so the modal only closes on success. */
  onNavigateToProject?: (projectId: ProjectId) => void | Promise<void> | Promise<boolean>;
  /** Optional: Override the default project click handler */
  onProjectClick?: (projectId: ProjectId) => void | Promise<void>;
  /** When provided, called when user clicks Back from the create form instead of switching to grid. Use to close the modal when opened directly in form view (e.g. from projects page). */
  onBackFromCreate?: () => void;
  /** When provided, called BEFORE the "Create project" action switches to the form view. Return false to abort (e.g. to show an auth gate for guest users). */
  onBeforeCreateProject?: (initialName?: string) => boolean;
}

// Almost every project is owned, so only a role short of ownership earns the row's width.
const roleLabel = (project: Project, strings: ProjectSwitcherStrings): string | undefined => {
  switch (project.role) {
    case "role.collaborator":
      return strings.roleCollaborator;
    case "role.reader":
      return strings.roleReader;
    default:
      return undefined;
  }
};

interface ProjectListViewProps {
  projects: ProjectsService;
  userContext: UserContextService;
  strings: ProjectSwitcherStrings;
  organizationName?: (organizationId: string) => string | undefined;
  onCreateProject: (initialName?: string) => void;
  onClose: () => void;
  onProjectClick?: (projectId: ProjectId) => void | Promise<void>;
}

const LISTBOX_ID = "project-switcher-results";
const optionId = (index: number) => `project-switcher-option-${index}`;

const ProjectListView: Component<ProjectListViewProps> = (props) => {
  const [query, setQuery] = createSignal("");
  const [activeIndex, setActiveIndex] = createSignal(0);
  let listRef: HTMLDivElement | undefined;
  let inputRef: HTMLInputElement | undefined;

  // A fresh array every run, so a memo would short-circuit on nothing.
  const results = () => rankProjects(props.projects.all(), query(), props.userContext.getRecentProjectIds());

  const trimmedQuery = () => query().trim();
  const activeProjectId = () => props.userContext.get()?.projectId;

  // The create row takes the option index after the last project, so arrow keys reach it too.
  const createIndex = () => results().length;

  const secondaryLine = (project: Project) =>
    [props.organizationName?.(project.organizationId), roleLabel(project, props.strings)].filter(Boolean).join(" · ");

  const scrollActiveIntoView = () => {
    const options = listRef?.querySelectorAll<HTMLElement>("[data-project-option]");
    options?.[activeIndex()]?.scrollIntoView({ block: "nearest" });
  };

  const moveActive = (delta: number) => {
    const count = createIndex() + 1;
    setActiveIndex((activeIndex() + delta + count) % count);
    scrollActiveIntoView();
  };

  const openProject = async (projectId: ProjectId, e?: MouseEvent) => {
    // Rows aren't anchors, so open-in-new-tab intent has to be read off the event.
    if (e && (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1)) {
      window.open(`/project/${projectId}`, "_blank", "noopener,noreferrer");
      return;
    }

    if (props.onProjectClick) {
      await props.onProjectClick(projectId);
      return;
    }

    if (activeProjectId() !== projectId) {
      props.userContext.switchToProject(projectId);
    }
    props.onClose();
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      moveActive(1);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      moveActive(-1);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const project = results()[activeIndex()];
      if (project) {
        void openProject(project.id);
      } else {
        props.onCreateProject(trimmedQuery() || undefined);
      }
    }
  };

  // Kobalte parks focus on the dialog container when it opens; take it back on
  // the next frame so the first keystroke lands in the search box.
  onMount(() => {
    const frame = requestAnimationFrame(() => inputRef?.focus());
    onCleanup(() => cancelAnimationFrame(frame));
  });

  return (
    <div class="flex min-h-0 flex-col">
      <div class="mb-3 flex min-h-7 items-center pr-16">
        <StDialogTitle class="text-lg font-semibold text-primary-text">{props.strings.modalTitle}</StDialogTitle>
      </div>

      <div class="relative mb-2">
        <TbSearch class="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-text" />
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          autocomplete="off"
          aria-expanded="true"
          aria-controls={LISTBOX_ID}
          aria-activedescendant={optionId(activeIndex())}
          aria-label={props.strings.searchPlaceholder}
          placeholder={props.strings.searchPlaceholder}
          value={query()}
          onInput={(e) => {
            setQuery(e.currentTarget.value);
            setActiveIndex(0);
          }}
          onKeyDown={handleKeyDown}
          class="w-full rounded-lg border border-primary-border bg-canvas py-2 pr-3 pl-10 text-sm text-primary-text outline-none placeholder:text-muted-text focus-visible:ring-2 focus-visible:ring-brand"
        />
      </div>

      {/* Rows are not tab stops: focus stays on the input and moves via aria-activedescendant. */}
      <div
        ref={listRef}
        id={LISTBOX_ID}
        role="listbox"
        aria-label={props.strings.modalTitle}
        class="max-h-[50vh] min-h-0 flex-1 overflow-y-auto"
      >
        <Show when={!trimmedQuery() && results().length > 0}>
          <p class="px-3 pt-1 pb-0.5 text-xs font-semibold tracking-wide text-secondary-text uppercase">
            {props.strings.recentHeading}
          </p>
        </Show>

        <For each={results()}>
          {(project, index) => {
            const isActive = () => activeIndex() === index();
            const isCurrent = () => activeProjectId() === project.id;
            return (
              <div
                id={optionId(index())}
                data-project-option
                role="option"
                tabIndex={-1}
                aria-selected={isActive()}
                aria-current={isCurrent() ? "true" : undefined}
                onClick={(e) => void openProject(project.id, e)}
                onAuxClick={(e) => {
                  // auxclick also fires for the right button, which must keep opening the context menu.
                  if (e.button === 1) void openProject(project.id, e);
                }}
                onMouseMove={() => setActiveIndex(index())}
                class={twMerge(
                  "flex cursor-pointer items-center gap-2.5 rounded-md px-3 py-2 text-left transition-colors",
                  isActive() && "bg-inset"
                )}
              >
                <TbCube class="size-4 shrink-0 text-muted-text" aria-hidden />
                <span class="min-w-0 flex-1">
                  <span class="block truncate text-sm text-primary-text">{project.name}</span>
                  <Show when={secondaryLine(project)}>
                    {(detail) => <span class="block truncate text-xs text-muted-text">{detail()}</span>}
                  </Show>
                </span>
                <Show when={isCurrent()}>
                  <span class="shrink-0 rounded-full border border-primary-border bg-tile px-2 py-0.5 text-xs font-medium text-secondary-text">
                    {props.strings.currentProject}
                  </span>
                </Show>
              </div>
            );
          }}
        </For>

        <Show when={results().length === 0}>
          <p class="px-3 py-6 text-center text-sm text-secondary-text">{props.strings.noResults}</p>
        </Show>

        <Show when={results().length > 0}>
          <div class="my-1 border-t border-primary-border" aria-hidden />
        </Show>

        <div
          id={optionId(createIndex())}
          data-project-option
          role="option"
          tabIndex={-1}
          aria-selected={activeIndex() === createIndex()}
          onClick={() => props.onCreateProject(trimmedQuery() || undefined)}
          onMouseMove={() => setActiveIndex(createIndex())}
          class={twMerge(
            "flex cursor-pointer items-center gap-2.5 rounded-md bg-brand px-3 py-2 text-left font-medium text-on-brand transition-colors",
            activeIndex() === createIndex() && "bg-brand-hover"
          )}
        >
          <TbCubePlus class="size-4 shrink-0" aria-hidden />
          <span class="min-w-0 flex-1 truncate text-sm">
            <Show when={trimmedQuery()} fallback={props.strings.addProject}>
              {props.strings.createNamed.replace("{name}", trimmedQuery())}
            </Show>
          </span>
        </div>
      </div>
    </div>
  );
};

interface CreateProjectFormViewProps {
  projects: ProjectsService;
  userContext: UserContextService;
  homeOrganizationId: () => string | undefined;
  authToken: () => string | undefined;
  createOrganizations?: () => { id: string; name: string }[];
  unavailableOrganizationName?: () => string | undefined;
  strings: ProjectSwitcherStrings;
  onBack: () => void;
  onSuccess: () => void;
  onNavigateToProject?: (projectId: ProjectId) => void | Promise<void> | Promise<boolean>;
  initialProjectName?: string;
}

// The client only sets `code` when the response carried the error envelope — without
// one the text is the raw HTTP status line, which is no use to a reader.
const humaneMessage = (error: Error): string | undefined => {
  const { code } = error as Error & { code?: string };
  return code ? serverMessage(error.message) : undefined;
};

const CreateProjectFormView: Component<CreateProjectFormViewProps> = (props) => {
  const client = useStorytellClient();

  // Form state
  const [projectName, setProjectName] = createSignal(props.initialProjectName || "");
  const [description, setDescription] = createSignal("");
  const [billingMode, setBillingMode] = createSignal<CreatableBillingMode>("caller");
  const [billingExpanded, setBillingExpanded] = createSignal(false);
  const [chosenOrganizationId, setChosenOrganizationId] = createSignal<string>();
  const [createError, setCreateError] = createSignal<string>();
  let projectNameInputRef: HTMLInputElement | undefined;

  const organizations = () => props.createOrganizations?.() ?? [];

  const defaultOrganizationId = () => {
    const home = props.homeOrganizationId();
    const list = organizations();
    if (list.length === 0) return home;
    return list.some((organization) => organization.id === home) ? home : list[0]?.id;
  };

  const organizationId = () => chosenOrganizationId() ?? defaultOrganizationId();

  // Sync project name when initialProjectName prop changes
  createEffect(() => {
    if (props.initialProjectName) {
      setProjectName(props.initialProjectName);
    }
  });

  // Auto-focus the project name input when component mounts
  onMount(() => {
    setTimeout(() => {
      projectNameInputRef?.focus();
      // If there's an initial project name, select all text so user can easily replace it
      if (props.initialProjectName && projectNameInputRef) {
        projectNameInputRef.select();
      }
    }, 50);
  });

  const handleSubmit = (e: Event) => {
    createProject(e).catch((error: unknown) => console.error("Failed to create project", error));
  };

  const createProject = async (e: Event) => {
    e.preventDefault();
    setCreateError(undefined);

    const token = props.authToken();
    if (!token) {
      console.error("Missing auth token");
      return;
    }

    const request = CreateProjectRequestConfig.withBearerToken(token);
    const orgId = organizationId();
    const response = await client.fetch(
      request,
      undefined,
      {
        name: projectName(),
        description: description(),
        billingMode: billingMode(),
      },
      orgId ? { orgIdOverride: orgId } : undefined
    );

    if (response.isErr()) {
      console.error("Failed to create project", response.error);
      setCreateError(humaneMessage(response.error) ?? props.strings.createError);
      return;
    }

    const newProject = response.value;
    props.projects.set(newProject.id, newProject);
    void props.projects.refetch();

    let switchSucceeded = true;
    if (props.onNavigateToProject) {
      const result = await props.onNavigateToProject(newProject.id);
      switchSucceeded = result === undefined ? true : result;
    } else {
      props.userContext.switchToProject(newProject.id, { preserveNavigation: true });
    }

    if (switchSucceeded) {
      props.onSuccess();
    }
  };

  return (
    <div class="flex h-full min-h-0 flex-col">
      <div class="mb-4">
        <StButton
          onClick={props.onBack}
          class="flex items-center gap-1 text-sm text-muted-text hover:text-primary-text"
        >
          <TbArrowLeft class="size-4" />
          {props.strings.back}
        </StButton>
      </div>

      <StDialogTitle class="mb-2 text-xl font-semibold">{props.strings.createTitle}</StDialogTitle>
      <p class="mb-6 text-sm text-muted-text">{props.strings.createDesc}</p>

      <div class="scrollbar-hide min-h-0 flex-1 overflow-y-auto p-0.5">
        {/* input and change bubble, so one pair of handlers clears the error for every field. */}
        <form
          onSubmit={handleSubmit}
          onInput={() => setCreateError(undefined)}
          onChange={() => setCreateError(undefined)}
          class="space-y-6"
        >
          <div class="space-y-4">
            <Show when={organizations().length > 0 || props.unavailableOrganizationName?.()}>
              <div>
                <Show when={organizations().length > 0}>
                  <label for="project-organization" class="mb-1.5 block text-sm font-medium">
                    {props.strings.organizationLabel}
                  </label>
                  <select
                    id="project-organization"
                    value={organizationId()}
                    onChange={(e) => setChosenOrganizationId(e.currentTarget.value)}
                    class="w-full rounded-lg border border-primary-border bg-canvas px-3 py-2 text-sm focus:ring-2 focus:ring-brand focus:outline-none"
                  >
                    <For each={organizations()}>
                      {(organization) => <option value={organization.id}>{organization.name}</option>}
                    </For>
                  </select>
                </Show>
                <Show when={props.unavailableOrganizationName?.()}>
                  {(name) => (
                    <p class="mt-1.5 text-xs text-muted-text">
                      {props.strings.organizationUnavailable.replace("{name}", name())}
                    </p>
                  )}
                </Show>
              </div>
            </Show>

            <div>
              <div class="mb-1.5 flex items-center justify-between">
                <label for="project-name" class="text-sm font-medium">
                  {props.strings.projectName}
                </label>
                <a
                  class="inline-flex items-center gap-1 text-xs text-brand underline hover:underline-offset-2"
                  href="https://go.chief.bot/learn-projects"
                  target="_blank"
                  rel="noreferrer"
                >
                  <TbHelpCircle class="size-3.5" />
                  {props.strings.learnProjects}
                </a>
              </div>
              <input
                ref={projectNameInputRef}
                id="project-name"
                type="text"
                value={projectName()}
                onInput={(e) => setProjectName(e.currentTarget.value)}
                placeholder={props.strings.namePlaceholder}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && projectName().trim()) {
                    e.preventDefault();
                    handleSubmit(e);
                  }
                }}
                class="w-full rounded-lg border border-primary-border bg-canvas px-3 py-2 text-sm focus:ring-2 focus:ring-brand focus:outline-none"
                required
              />
            </div>

            <div>
              <label for="project-description" class="mb-1.5 block text-sm font-medium">
                {props.strings.description}{" "}
                <span class="font-normal text-muted-text">{props.strings.descriptionOptional}</span>
              </label>
              <textarea
                id="project-description"
                value={description()}
                onInput={(e) => setDescription(e.currentTarget.value)}
                placeholder={props.strings.descriptionPlaceholder}
                rows={3}
                class="w-full resize-none rounded-lg border border-primary-border bg-canvas px-3 py-2 text-sm focus:ring-2 focus:ring-brand focus:outline-none"
              />
            </div>

            <div>
              <div class="mb-1.5 flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => setBillingExpanded((v) => !v)}
                  aria-expanded={billingExpanded()}
                  class="inline-flex items-center gap-1 text-sm font-medium"
                >
                  <TbChevronDown
                    class={twMerge("size-4 transition-transform", billingExpanded() ? "" : "-rotate-90")}
                  />
                  {props.strings.billingHeading}
                </button>
                <a
                  class="inline-flex items-center gap-1 text-xs text-brand underline hover:underline-offset-2"
                  href="https://go.chief.bot/learn-org-billing"
                  target="_blank"
                  rel="noreferrer"
                >
                  <TbHelpCircle class="size-3.5" />
                  {props.strings.billingLearnMore}
                </a>
              </div>
              <Show when={billingExpanded()}>
                <div class="space-y-2">
                  <label class="flex cursor-pointer items-start gap-3 rounded-lg border border-primary-border bg-canvas px-3 py-2">
                    <input
                      type="radio"
                      name="billing-mode"
                      class="mt-1"
                      checked={billingMode() === "caller"}
                      onChange={() => setBillingMode("caller")}
                    />
                    <span>
                      <span class="block text-sm font-medium">{props.strings.billingCallerLabel}</span>
                      <span class="block text-xs text-muted-text">{props.strings.billingCallerDesc}</span>
                    </span>
                  </label>
                  <label class="flex cursor-pointer items-start gap-3 rounded-lg border border-primary-border bg-canvas px-3 py-2">
                    <input
                      type="radio"
                      name="billing-mode"
                      class="mt-1"
                      checked={billingMode() === "org"}
                      onChange={() => setBillingMode("org")}
                    />
                    <span>
                      <span class="block text-sm font-medium">{props.strings.billingOrgLabel}</span>
                      <span class="block text-xs text-muted-text">{props.strings.billingOrgDesc}</span>
                    </span>
                  </label>
                </div>
              </Show>
            </div>
          </div>
        </form>
      </div>

      <Show when={createError()}>
        {(message) => (
          <p class="pt-4 text-sm text-red-600 dark:text-red-400" role="alert">
            {message()}
          </p>
        )}
      </Show>

      {/* Fixed Footer */}
      <div class="flex items-center gap-3 border-t border-primary-border pt-4">
        <StButton type="button" onClick={props.onBack} class="flex-1">
          {props.strings.cancel}
        </StButton>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!projectName().trim()}
          class="flex-1 rounded-lg border border-primary-border bg-brand px-4 py-2 text-sm font-medium text-on-brand transition-colors hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-50"
        >
          {props.strings.createButton}
        </button>
      </div>
    </div>
  );
};

export const ProjectSwitcherModal: Component<ProjectSwitcherModalProps> = (props) => {
  const [view, setView] = createSignal<ViewState>(props.initialView || "grid");
  const [initialProjectName, setInitialProjectName] = createSignal<string | undefined>(undefined);

  const strings = createMemo((): ProjectSwitcherStrings => ({
    ...DEFAULT_STRINGS,
    ...props.strings,
  }));

  const handleNavigateToProject = (projectId: ProjectId) => props.onNavigateToProject?.(projectId);

  const handleModalChange = (open: boolean) => {
    if (!open) {
      setView("grid");
      // Restore focus when modal closes so keyboard shortcuts work
      setTimeout(() => {
        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
        const mainContent = document.querySelector("main") || document.body;
        if (mainContent instanceof HTMLElement) {
          if (!mainContent.hasAttribute("tabindex")) {
            mainContent.setAttribute("tabindex", "-1");
          }
          mainContent.focus();
        }
        if (typeof window !== "undefined") {
          window.focus();
        }
      }, 200);
    }
    props.onOpenChange(open);
  };

  // Sync view with initialView prop each time the modal transitions from closed to open
  let prevOpen = props.open;
  createEffect(() => {
    const isOpen = props.open;
    if (isOpen && !prevOpen) {
      setView(props.initialView || "grid");
    }
    prevOpen = isOpen;
  });

  // Handle ESC key when modal is open
  createEffect(() => {
    if (!props.open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        handleModalChange(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown, { capture: true });

    onCleanup(() => {
      document.removeEventListener("keydown", handleKeyDown, { capture: true });
    });
  });

  return (
    <StDialog
      open={props.open}
      onOpenChange={handleModalChange}
      overlayProps={{
        class: "fixed z-[110] inset-0 bg-black/50 dark:bg-white/10",
      }}
      wrapperProps={{
        class: "fixed z-[110] inset-0 flex items-center justify-center pointer-events-none",
      }}
      contentProps={{
        class: "max-w-xl flex flex-col max-h-[80vh]",
      }}
      content={
        <>
          <StDialogCloseButton as={StButton} class="absolute top-8 right-8 z-10 size-7">
            <TbX class="size-4" />
          </StDialogCloseButton>

          <Show
            when={view() === "grid"}
            fallback={
              <CreateProjectFormView
                projects={props.projects}
                userContext={props.userContext}
                homeOrganizationId={props.homeOrganizationId}
                authToken={props.authToken}
                createOrganizations={props.createOrganizations}
                unavailableOrganizationName={props.unavailableOrganizationName}
                strings={strings()}
                onBack={
                  props.onBackFromCreate ??
                  (() => {
                    setView("grid");
                    setInitialProjectName(undefined);
                  })
                }
                onSuccess={() => {
                  setInitialProjectName(undefined);
                  handleModalChange(false);
                }}
                onNavigateToProject={handleNavigateToProject}
                initialProjectName={initialProjectName()}
              />
            }
          >
            <ProjectListView
              projects={props.projects}
              userContext={props.userContext}
              strings={strings()}
              organizationName={props.organizationName}
              onProjectClick={props.onProjectClick}
              onCreateProject={(initialName) => {
                if (props.onBeforeCreateProject && !props.onBeforeCreateProject(initialName)) return;
                setInitialProjectName(initialName);
                setView("form");
              }}
              onClose={() => handleModalChange(false)}
            />
          </Show>
        </>
      }
    />
  );
};
